/**
 * SIsList Component
 * Main container for Supplemental Instructions management.
 * Follows RFIsList pattern.
 */
import { useState } from 'react';
import { SI } from '@/types/supplemental_instructions';
import { SICard } from './SICard';
import { AddSIModal } from './AddSIModal';
import { SIDetailPanel } from './SIDetailPanel';
import { Button } from '@/components/ui/button';
import { Plus, FileText } from 'lucide-react';
import { useSIs, useDeleteSI } from '@/hooks';
import { EmptyState } from '@/components/ui/empty-state';

interface SIsListProps {
  projectId: string;
  hideHeader?: boolean;
}

export function SIsList({ projectId, hideHeader = false }: SIsListProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSI, setEditingSI] = useState<SI | null>(null);
  const [selectedSIId, setSelectedSIId] = useState<string | null>(null);

  const { data, isLoading } = useSIs(projectId);
  const sis = data?.sis ?? [];
  const deleteSI = useDeleteSI();

  const handleCardClick = (si: SI) => {
    setSelectedSIId(si.id);
  };

  const handleEdit = (si: SI) => {
    setSelectedSIId(null);
    setEditingSI(si);
    setIsModalOpen(true);
  };

  const handleDelete = async (siId: string) => {
    if (confirm('Are you sure you want to delete this Supplemental Instruction?')) {
      await deleteSI.mutateAsync(siId);
    }
  };

  const handleAddNew = () => {
    setEditingSI(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingSI(null);
  };

  if (isLoading) {
    return <div>Loading Supplemental Instructions...</div>;
  }

  // Issue #96: When the empty state renders its own "Create SI" CTA, suppress
  // the duplicate header "Add SI" button. Header still shows the title row when
  // hideHeader=false (so empty users keep one heading + one CTA, not two CTAs).
  const isEmpty = sis.length === 0;

  return (
    <div className="space-y-4">
      {!hideHeader ? (
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Supplemental Instructions ({sis.length})</h3>
          {!isEmpty && (
            <Button onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add SI
            </Button>
          )}
        </div>
      ) : (
        !isEmpty && (
          <div className="flex justify-end">
            <Button onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add SI
            </Button>
          </div>
        )
      )}

      {isEmpty ? (
        <EmptyState
          icon={FileText}
          title="No Supplemental Instructions yet"
          description="Issue supplemental instructions to clarify or supplement contract documents"
          actionLabel="Create SI"
          onAction={handleAddNew}
        />
      ) : (
        <div className="space-y-2">
          {sis.map((si) => (
            <SICard
              key={si.id}
              si={si}
              onClick={handleCardClick}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <AddSIModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        projectId={projectId}
        editingSI={editingSI}
      />

      <SIDetailPanel
        siId={selectedSIId}
        onClose={() => setSelectedSIId(null)}
        onEdit={handleEdit}
      />
    </div>
  );
}
